import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { Button } from "@shared/ui/button";
import { Card, CardContent } from "@shared/ui/card";
import { Switch } from "@shared/ui/switch";
import { Input } from "@shared/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { Badge } from "@shared/ui/badge";
import { Alert, AlertDescription } from "@shared/ui/alert";
import {
  Pencil,
  Plus,
  Search,
  Calendar,
  User,
  Check,
  X,
  HardHat,
  Loader2,
  AlertCircle,
  RefreshCw,
} from "lucide-react";
import { useToast } from "@shared/hooks/use-toast";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import {
  usePPEEquipment,
  useCreatePPEEquipment,
  useUpdatePPEEquipment,
  useTogglePPEEquipmentStatus,
} from "../../../../hooks";
import { PPEEquipment } from "../../../../types";

// ====================== TYPES ======================

interface PPEEquipmentFormData {
  name: string;
  description: string;
}

// ====================== HOOKS ======================

// Custom hook for debounced search
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  React.useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// ====================== COMPONENTS ======================

// PPE Equipment card component for better reusability
const PPEEquipmentCard: React.FC<{
  equipment: PPEEquipment;
  onEdit: (equipment: PPEEquipment) => void;
  onToggleActive: (equipment: PPEEquipment) => void;
  isUpdating: boolean;
  currentEquipment: PPEEquipment | null;
}> = ({ equipment, onEdit, onToggleActive, isUpdating, currentEquipment }) => {
  return (
    <Card className="overflow-hidden transition-all hover:shadow-md hover:border-primary/20 group">
      <CardContent className="p-0">
        <div className="p-4 border-b bg-muted/20">
          <div className="flex justify-between items-start">
            <div className="flex items-start gap-2">
              <HardHat className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <h5 className="font-medium">{equipment.name}</h5>

                <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                  {equipment.description}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEdit(equipment)}
              className="opacity-0 group-hover:opacity-100 hover:bg-background/80"
              disabled={isUpdating}
            >
              {isUpdating && currentEquipment?.id === equipment.id ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Pencil className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>

        <div className="p-4">
          <div className="flex gap-2 items-center text-sm text-muted-foreground">
            <User className="h-3.5 w-3.5" />
            <p>{equipment.createdBy}</p>
          </div>
          <div className="flex gap-2 items-center text-sm text-muted-foreground mt-1">
            <Calendar className="h-3.5 w-3.5" />
            <p>{equipment.createdDate}</p>
          </div>

          <div className="flex items-center justify-between mt-4 pt-4 border-t">
            <span className="text-sm">
              {equipment.isActive ? (
                <span className="flex items-center gap-1.5 text-green-600 bg-green-50 dark:bg-green-950/30 px-2 py-1 rounded-full text-xs font-medium">
                  <Check className="h-3 w-3" /> Active
                </span>
              ) : (
                <span className="flex items-center gap-1.5 text-gray-600 bg-gray-50 dark:bg-gray-800/30 px-2 py-1 rounded-full text-xs font-medium">
                  <X className="h-3 w-3" /> Inactive
                </span>
              )}
            </span>
            <Switch
              checked={equipment.isActive}
              onCheckedChange={() => onToggleActive(equipment)}
              disabled={isUpdating}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Empty state component
const EmptyState: React.FC<{
  hasSearchQuery: boolean;
  onAddNew: () => void;
}> = ({ hasSearchQuery, onAddNew }) => {
  return (
    <div className="col-span-full py-12 text-center text-muted-foreground bg-muted/20 rounded-lg border border-dashed">
      <div className="flex flex-col items-center gap-2">
        <HardHat className="h-10 w-10 text-muted-foreground/50" />
        <h4>No PPE equipment found</h4>
        <p className="text-sm">
          {hasSearchQuery
            ? "Try adjusting your search or add new PPE equipment"
            : "No PPE equipment available"}
        </p>
        <Button variant="outline" className="mt-2" onClick={onAddNew}>
          <Plus className="mr-2 h-4 w-4" /> Add New Equipment
        </Button>
      </div>
    </div>
  );
};

// ====================== MAIN COMPONENT ======================

const PPEEquipmentTab: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingEquipment, setEditingEquipment] = useState<PPEEquipment | null>(
    null
  );
  
  // Add state to track if we're in cancel mode
  const [isCanceling, setIsCanceling] = useState(false);

  // Get search query from URL
  const searchQuery = searchParams.get("search") || "";

  // Debounce the search input for better UX (but server handles the actual search)
  const [searchInput, setSearchInput] = useState(searchQuery);
  const debouncedSearchInput = useDebounce(searchInput, 500);

  // Update URL when debounced search changes
  useEffect(() => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (debouncedSearchInput.trim()) {
      newSearchParams.set("search", debouncedSearchInput.trim());
    } else {
      newSearchParams.delete("search");
    }
    setSearchParams(newSearchParams, { replace: true });
  }, [debouncedSearchInput, searchParams, setSearchParams]);

  // API hooks - now using server-side search
  const { data: equipment = [] } = usePPEEquipment(searchQuery);

  const createEquipmentMutation = useCreatePPEEquipment();
  const updateEquipmentMutation = useUpdatePPEEquipment();
  const toggleStatusMutation = useTogglePPEEquipmentStatus();

  // Form fields configuration
  const formFields: FormField[] = useMemo(
    () => [
      {
        name: "name",
        label: "Equipment Name",
        type: "text",
        required: true,
        placeholder: "Enter equipment name",
        minLength: 3,
        maxLength: 100,
        helperText: "Provide a clear, descriptive name for the PPE equipment",
      },
      {
        name: "description",
        label: "Description",
        type: "textarea",
        placeholder: "Describe this PPE equipment",
        rows: 3,
        maxLength: 500,
        helperText:
          "Detailed description of the equipment and its protective functions",
        className: "pt-2",
      },
    ],
    []
  );

  // Create form instance using FormService
  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: {
      name: "",
      description: "",
    },
  });

  // Update form when formData changes - but not during cancel
  useEffect(() => {
    if (isCanceling) return; // Skip form updates during cancel
    
    if (editingEquipment && isEditModalOpen) {
      const populateData = {
        name: editingEquipment.name,
        description: editingEquipment.description,
      };
      FormService.populateForm(form, populateData, formFields);
    } else if (isAddModalOpen) {
      const populateData = {
        name: "",
        description: "",
      };
      FormService.populateForm(form, populateData, formFields);
    }
  }, [editingEquipment, isEditModalOpen, isAddModalOpen, form, formFields, isCanceling]);

  // Handle field changes
  const handleFieldChange = useCallback((fieldName: string, value: any) => {
    // Prevent any field changes during cancel
    if (isCanceling) return;
  }, [isCanceling]);

  // Handle form submission
  const handleSubmit = async (data: PPEEquipmentFormData) => {
    if (isCanceling) return; // Prevent submit during cancel
    
    try {
      if (editingEquipment) {
        const updatePayload = {
          id: editingEquipment.id,
          name: data.name.trim(),
          description: data.description.trim(),
          type: undefined, // Will be undefined, but that's fine for the API
        };
        // Update existing equipment
        await updateEquipmentMutation.mutateAsync(updatePayload);
        setIsEditModalOpen(false);
      } else {
        const createPayload = {
          name: data.name.trim(),
          description: data.description.trim(),
          type: undefined, // Will be undefined, but that's fine for the API
        };
        // Create new equipment
        await createEquipmentMutation.mutateAsync(createPayload);
        setIsAddModalOpen(false);
      }

      FormService.resetForm(form, formFields);
      setEditingEquipment(null);
    } catch (error) {
      // Handle error
    }
  };

  const handleReset = useCallback(() => {
    if (isCanceling) return; // Prevent reset during cancel
    FormService.resetForm(form, formFields);
  }, [form, formFields, isCanceling]);

  // Handle edit
  const handleEdit = useCallback((item: PPEEquipment) => {
    setEditingEquipment(item);
    setIsEditModalOpen(true);
  }, []);

  // Handle toggle status
  const handleToggleStatus = useCallback(
    async (item: PPEEquipment) => {
      try {
        await toggleStatusMutation.mutateAsync({
          id: item.id,
          isActive: !item.isActive,
          name: item.name,
        });
      } catch (error) {
        // Handle error
      }
    },
    [toggleStatusMutation]
  );

  // Handle modal close with cancel protection
  const handleAddModalClose = useCallback(() => {
    setIsCanceling(true);
    setIsAddModalOpen(false);
    setEditingEquipment(null);
    
    // Reset form without triggering side effects
    setTimeout(() => {
      FormService.resetForm(form, formFields);
      setIsCanceling(false);
    }, 0);
  }, [form, formFields]);

  const handleEditModalClose = useCallback(() => {
    setIsCanceling(true);
    setIsEditModalOpen(false);
    setEditingEquipment(null);
    
    // Reset form without triggering side effects
    setTimeout(() => {
      FormService.resetForm(form, formFields);
      setIsCanceling(false);
    }, 0);
  }, [form, formFields]);

  // Handle search input change
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchInput(e.target.value);
    },
    []
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-start flex-col ">
          <h4 className="text-lg font-medium">PPE Equipment</h4>
          <p className="page-subtitle">Manage and track personal protective gear.</p>
        </div>
        <Button
          onClick={() => setIsAddModalOpen(true)}
          disabled={createEquipmentMutation.isPending}
        >
          {createEquipmentMutation.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Plus className="mr-2 h-4 w-4" />
          )}
          Add New Equipment
        </Button>
      </div>

      {/* Equipment Grid */}
      <div className="relative pb-4">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search PPE equipment..."
              className="pl-8 w-full sm:w-[250px]"
              value={searchInput}
              onChange={handleSearchChange}
            />
          </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {Array.isArray(equipment) &&
          equipment.map((item) => (
            <PPEEquipmentCard
              key={item.id}
              equipment={item}
              onEdit={handleEdit}
              onToggleActive={handleToggleStatus}
              isUpdating={
                updateEquipmentMutation.isPending ||
                toggleStatusMutation.isPending
              }
              currentEquipment={editingEquipment}
            />
          ))}

        {Array.isArray(equipment) && equipment.length === 0 && (
          <EmptyState
            hasSearchQuery={!!searchQuery}
            onAddNew={() => setIsAddModalOpen(true)}
          />
        )}
      </div>

      {/* Add Equipment Modal */}
      <Dialog open={isAddModalOpen} onOpenChange={handleAddModalClose}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HardHat className="h-5 w-5" />
              Create New PPE Equipment
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 pr-4">
            <DynamicForm
              fields={formFields}
              form={form}
              onSubmit={handleSubmit}
              onReset={handleReset}
              onFieldChange={handleFieldChange}
              loading={createEquipmentMutation.isPending}
              config={{
                showResetButton: false,
                showSubmitButton: false,
                resetOnSubmit: false,
                classes: {
                  form: "space-y-4",
                  container: "space-y-2",
                  label: "block text-sm font-medium text-gray-900 mb-1",
                  input:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed",
                  textarea:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical disabled:bg-gray-100 disabled:cursor-not-allowed",
                  error: "text-sm text-red-600 mt-1",
                  helperText: "text-xs text-muted-foreground mt-1",
                  required: "text-red-500 ml-1",
                },
              }}
              renderCustomFooter={() => (
                <div className="flex justify-end gap-2 pt-4">
                  <Button
                    variant="outline"
                    onClick={handleAddModalClose}
                    disabled={createEquipmentMutation.isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={form.handleSubmit(handleSubmit)}
                    disabled={createEquipmentMutation.isPending}
                  >
                    {createEquipmentMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      "Create Equipment"
                    )}
                  </Button>
                </div>
              )}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Equipment Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={handleEditModalClose}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HardHat className="h-5 w-5" />
              Edit PPE Equipment
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 pr-4">
            <DynamicForm
              fields={formFields}
              form={form}
              onSubmit={handleSubmit}
              onReset={handleReset}
              onFieldChange={handleFieldChange}
              loading={updateEquipmentMutation.isPending}
              config={{
                showResetButton: false,
                showSubmitButton: false,
                resetOnSubmit: false,
                classes: {
                  form: "space-y-4",
                  container: "space-y-2",
                  label: "block text-sm font-medium text-gray-900 mb-1",
                  input:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed",
                  textarea:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical disabled:bg-gray-100 disabled:cursor-not-allowed",
                  error: "text-sm text-red-600 mt-1",
                  helperText: "text-xs text-muted-foreground mt-1",
                  required: "text-red-500 ml-1",
                },
              }}
              renderCustomFooter={() => (
                <div className="flex justify-end gap-2 pt-4">
                  <Button
                    variant="outline"
                    onClick={handleEditModalClose}
                    disabled={updateEquipmentMutation.isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={form.handleSubmit(handleSubmit)}
                    disabled={updateEquipmentMutation.isPending}
                  >
                    {updateEquipmentMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Updating...
                      </>
                    ) : (
                      "Update Equipment"
                    )}
                  </Button>
                </div>
              )}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PPEEquipmentTab;